import { MatchClaims, MatchPhone } from "refsix-js-models";

export type ClaimKind = keyof MatchClaims;

export const NO_CLAIMS: MatchClaims = { goals: 0, assists: 0 };

// One more goal or assist for the user; null once every team goal is claimed
export const claimOne = (
    claims: MatchClaims,
    kind: ClaimKind,
    teamGoals: number
): MatchClaims | null =>
    claims.goals + claims.assists >= teamGoals
        ? null
        : { ...claims, [kind]: claims[kind] + 1 };

export const releaseOne = (claims: MatchClaims, kind: ClaimKind): MatchClaims =>
    claims[kind] ? { ...claims, [kind]: claims[kind] - 1 } : claims;

/**
 * Fits the claims to a lowered score. Assists go before goals, and `dropped`
 * says which kind had to go so the user is told rather than losing a claim
 * silently (goals win when both did).
 */
export const trimClaimsToScore = (
    claims: MatchClaims,
    teamGoals: number
): { claims: MatchClaims; dropped?: ClaimKind } => {
    const over = claims.goals + claims.assists - teamGoals;
    if (over <= 0) return { claims };
    const assistsCut = Math.min(over, claims.assists);
    const goalsCut = over - assistsCut;
    return {
        claims: {
            goals: claims.goals - goalsCut,
            assists: claims.assists - assistsCut,
        },
        dropped: goalsCut ? "goals" : "assists",
    };
};

/**
 * The user's claimed goals and assists across this season's other finished
 * matches, so the edit screen can show the season total ticking up.
 */
export const seasonClaimTotals = (
    matches: MatchPhone[],
    seasonStart: string | undefined,
    excludeMatchId: string
): MatchClaims => {
    const from = seasonStart ? new Date(seasonStart).getTime() : 0;
    return matches
        .filter(
            (match) =>
                match._id !== excludeMatchId &&
                match.matchFinished &&
                new Date(match.date as string).getTime() >= from
        )
        .reduce(
            (totals, match) => ({
                goals: totals.goals + (match.stats?.myGoalsTotal || 0),
                assists: totals.assists + (match.stats?.myAssistsTotal || 0),
            }),
            NO_CLAIMS
        );
};
